import { CLASS_NAMES, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * Duration of the number count-up animation in milliseconds.
 * @type {number}
 */
const COUNT_UP_DURATION = 900;

/**
 * Duration of the image wipe reveal in milliseconds. Passed to the
 * stylesheet as a CSS custom property so the transition timing is
 * controlled from here alongside the count-up duration.
 * @type {number}
 */
const IMAGE_REVEAL_DURATION = 1200;

/**
 * Maximum vertical offset in pixels between two number elements for
 * them to be considered part of the same visual row, so numbers that
 * sit side by side start counting up at the same moment.
 * @type {number}
 */
const ROW_ALIGNMENT_TOLERANCE = 10;

/**
 * Percentage of a number element that must be visible in the
 * viewport before its count-up is triggered.
 * @type {number}
 */
const NUMBER_OBSERVER_THRESHOLD = 0.9;

/**
 * Images trigger as soon as any part of them enters the trigger zone,
 * rather than waiting for a percentage of the element to be visible.
 * A percentage threshold scales with image height, so tall images
 * (and images whose wrapper includes a caption) triggered very late,
 * and could never trigger at all on short viewports.
 * @type {number}
 */
const IMAGE_OBSERVER_THRESHOLD = 0;

/**
 * Shrinks the bottom of the image trigger zone so the reveal starts
 * once the top of the image has scrolled 10% of the viewport height
 * into view. Being viewport-relative, this behaves consistently
 * across all breakpoints regardless of the image's rendered size.
 * Use a smaller value (e.g. '-5%') to trigger even earlier.
 * @type {string}
 */
const IMAGE_OBSERVER_ROOT_MARGIN = '0px 0px -10% 0px';

/**
 * A class for the Animated Impact Module entrance animations.
 * Supports multiple number and image elements within one module.
 * Each element is observed independently — numbers count up to their
 * final value once they are almost fully visible, and images wipe on
 * from one edge as soon as their top edge scrolls into view.
 */
export default class AnimatedImpactModule extends MessageBus {
  /**
   * Constructor function of the AnimatedImpactModule class. Must receive one
   * argument, a DOM node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * Uses querySelectorAll so every number and image in the module is
   * captured, not just the first one.
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    // querySelectorAll returns a NodeList of ALL matching elements
    this.numbers = Array.from(
      this.element.querySelectorAll(
        `.${SELECTORS.ANIMATED_IMPACT_MODULE_NUMBER}`
      )
    );

    this.images = Array.from(
      this.element.querySelectorAll(
        `.${SELECTORS.ANIMATED_IMPACT_MODULE_IMAGE}`
      )
    );

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleNumberIntersection = this.onNumberIntersection.bind(this);
    this.handleImageIntersection = this.onImageIntersection.bind(this);

    return this;
  }

  /**
   * Parses each number element, primes numbers and images to their
   * starting state and attaches separate IntersectionObservers for
   * numbers and images, as each needs its own trigger point.
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.numbers.forEach((number) => this.prepareNumber(number));

    // Fallback — if IntersectionObserver is unsupported, render final
    // values and leave images fully visible in their natural state
    if (!('IntersectionObserver' in window)) {
      this.numbers.forEach((number) => this.setFinalValue(number));

      return this;
    }

    this.images.forEach((image) => this.prepareImage(image));

    this.numberObserver = new IntersectionObserver(
      this.handleNumberIntersection,
      {
        threshold: NUMBER_OBSERVER_THRESHOLD
      }
    );

    this.imageObserver = new IntersectionObserver(
      this.handleImageIntersection,
      {
        rootMargin: IMAGE_OBSERVER_ROOT_MARGIN,
        threshold: IMAGE_OBSERVER_THRESHOLD
      }
    );

    this.numbers.forEach((number) => this.numberObserver.observe(number));
    this.images.forEach((image) => this.imageObserver.observe(image));

    return this;
  }

  /**
   * Reads the rendered value of a number element and stores its numeric
   * target, non-numeric prefix/suffix (e.g. "$", "%", "+"), decimal places
   * and thousands-separator usage as data attributes. The element is then
   * primed to zero so it can visibly count up from the start.
   * @param {HTMLElement} numberParam The number element to prepare
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   */
  prepareNumber(numberParam) {
    const number = numberParam;
    const rawText = number.textContent.trim();
    const match = rawText.match(/(\d[\d,]*(?:\.\d+)?)/);

    if (!match) return this;

    const [numericText] = match;
    const decimals = numericText.includes('.')
      ? numericText.split('.')[1].length
      : 0;

    number.dataset.target = numericText.replace(/,/g, '');
    number.dataset.prefix = rawText.slice(0, match.index);
    number.dataset.suffix = rawText.slice(match.index + numericText.length);
    number.dataset.decimals = `${decimals}`;
    number.dataset.separator = numericText.includes(',') ? 'true' : 'false';

    number.setAttribute('aria-label', rawText);
    number.textContent = AnimatedImpactModule.formatValue(number, 0);

    return this;
  }

  /**
   * Primes an image element for its wipe reveal by adding the
   * animatable state class, which clips it away from one edge via CSS.
   * Skipped entirely when the user prefers reduced motion so the image
   * simply remains visible in place.
   * @param {HTMLElement} imageParam The image element to prepare
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   */
  prepareImage(imageParam) {
    const image = imageParam;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) return this;

    image.style.setProperty(
      '--animated-impact-image-duration',
      `${IMAGE_REVEAL_DURATION}ms`
    );
    image.classList.add(CLASS_NAMES.IS_ANIMATABLE);

    return this;
  }

  /**
   * Handles intersection changes for number elements. Only the number
   * that has crossed the visibility threshold is animated, along with
   * any numbers sharing its visual row so side-by-side stats never
   * count up out of sync. Every number is guarded and unobserved after
   * triggering so its animation runs only once.
   * @param {IntersectionObserverEntry[]} entries The observed entries
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   */
  onNumberIntersection(entries) {
    entries.forEach((entryParam) => {
      const { target } = entryParam;

      if (!entryParam.isIntersecting) return;
      if (target.dataset.hasAnimated === 'true') return;

      // Start the triggered number and any row siblings together
      [target, ...this.getRowSiblings(target)].forEach((numberParam) => {
        const number = numberParam;

        if (number.dataset.hasAnimated === 'true') return;

        number.dataset.hasAnimated = 'true';
        this.numberObserver.unobserve(number);
        this.animateNumber(number);
      });
    });

    return this;
  }

  /**
   * Handles intersection changes for image elements. Each image is
   * revealed as soon as it enters the trigger zone, then unobserved so
   * the reveal runs only once.
   * @param {IntersectionObserverEntry[]} entries The observed entries
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   */
  onImageIntersection(entries) {
    entries.forEach((entryParam) => {
      const { target } = entryParam;

      if (!entryParam.isIntersecting) return;
      if (target.dataset.hasAnimated === 'true') return;

      target.dataset.hasAnimated = 'true';
      this.imageObserver.unobserve(target);
      this.revealImage(target);
    });

    return this;
  }

  /**
   * Finds all other number elements that have not yet animated and sit
   * on the same visual row as the given number, comparing viewport top
   * positions within a small tolerance. This prevents side-by-side
   * numbers of differing block heights from crossing the visibility
   * threshold at different scroll positions and counting up out of
   * sync.
   * @param {HTMLElement} target The number element that crossed the threshold
   * @returns {HTMLElement[]} Numbers sharing the same visual row as the target
   */
  getRowSiblings(target) {
    const targetTop = target.getBoundingClientRect().top;

    return this.numbers.filter(
      (number) =>
        number !== target &&
        number.dataset.hasAnimated !== 'true' &&
        Math.abs(number.getBoundingClientRect().top - targetTop) <=
          ROW_ALIGNMENT_TOLERANCE
    );
  }

  /**
   * Reveals the given image element by adding the revealed state class,
   * triggering the CSS transition that animates the clip inset back to
   * zero, wiping the image on from its starting edge.
   * @param {HTMLElement} imageParam The image element to reveal
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   */
  revealImage(imageParam) {
    const image = imageParam;

    image.classList.add(CLASS_NAMES.IS_REVEALED);

    return this;
  }

  /**
   * Animates the given number element from zero up to its target value
   * using requestAnimationFrame with an ease-out curve. Respects the
   * user's reduced-motion preference by rendering the final value
   * immediately.
   * @param {HTMLElement} numberParam The number element to animate
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   */
  animateNumber(numberParam) {
    const number = numberParam;
    const target = parseFloat(number.dataset.target);

    if (Number.isNaN(target)) return this;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      this.setFinalValue(number);

      return this;
    }

    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / COUNT_UP_DURATION, 1);
      // Ease-out cubic — fast start, gentle landing on the final number
      const eased = 1 - (1 - progress) ** 3;

      number.textContent = AnimatedImpactModule.formatValue(
        number,
        target * eased
      );

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        this.setFinalValue(number);
      }
    };

    window.requestAnimationFrame(step);

    return this;
  }

  /**
   * Renders the final target value of the given number element.
   * @param {HTMLElement} numberParam The number element to finalize
   * @returns {AnimatedImpactModule} A reference to the current instance of the class
   */
  setFinalValue(numberParam) {
    const number = numberParam;
    const target = parseFloat(number.dataset.target);

    if (Number.isNaN(target)) return this;

    number.textContent = AnimatedImpactModule.formatValue(number, target);

    return this;
  }

  /**
   * Formats a numeric value using the prefix, suffix, decimal places
   * and thousands-separator settings stored on the element, so values
   * like "$1,200+" or "99.5%" animate without losing their formatting.
   * @param {HTMLElement} number The number element providing format settings
   * @param {number} value The numeric value to format
   * @returns {string} The fully formatted display string
   */
  static formatValue(number, value) {
    const decimals = parseInt(number.dataset.decimals, 10) || 0;
    const useSeparator = number.dataset.separator === 'true';

    let formatted = value.toFixed(decimals);

    if (useSeparator) {
      const [integerPart, decimalPart] = formatted.split('.');
      const withCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      formatted = decimalPart ? `${withCommas}.${decimalPart}` : withCommas;
    }

    return `${number.dataset.prefix}${formatted}${number.dataset.suffix}`;
  }
}
